import type { Event } from '@sentry/vue'

const isExtensionSource = (source: string) =>
  /^(?:chrome-extension|moz-extension|safari-web-extension):\/\//.test(source)

export function isKnownBrowserError(event: Event, userAgent: string): boolean {
  const exceptions = event.exception?.values ?? []
  const frames = exceptions.flatMap(value => value.stacktrace?.frames ?? [])
  const sources = frames
    .map(frame => frame.filename ?? frame.abs_path ?? '')
    .filter(source => source && source !== '[native code]')

  // Extension URLs can also contain directories such as /utils/ or /components/.
  // Every exception needs its own evidence, including linked/aggregate errors.
  if (
    exceptions.length > 0 &&
    exceptions.every(
      exception =>
        exception.stacktrace?.frames?.some(frame =>
          isExtensionSource(frame.filename ?? frame.abs_path ?? '')
        )
    ) &&
    frames.every(frame => {
      const source = frame.filename ?? frame.abs_path ?? ''
      return source === '[native code]' || isExtensionSource(source)
    })
  ) {
    return true
  }

  // Never discard a mixed stack containing application code.
  if (
    sources.some(source =>
      /(?:^|\/)(?:_nuxt|components|composables|utils|plugins)\//.test(source)
    )
  ) {
    return false
  }

  if (exceptions.length !== 1) return false

  return exceptions.some(({ value }) => {
    if (
      value === 'Error invoking postMessage: Java object is gone' &&
      sources.length > 0 &&
      sources.every(source =>
        source.startsWith('iabjs://navigation_performance_logger_android')
      )
    ) {
      return true
    }

    if (
      value ===
        "undefined is not an object (evaluating 'window.webkit.messageHandlers')" &&
      /Instagram/i.test(userAgent) &&
      frames.length > 0 &&
      frames.every(frame => frame.lineno === 1)
    ) {
      return true
    }

    if (value === 'Invalid call to runtime.sendMessage(). Tab not found.') {
      return frames.length === 0
    }

    return (
      value ===
        "undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')" &&
      /Brave/i.test(userAgent) &&
      frames.length === 1 &&
      frames[0].function === 'global code' &&
      frames[0].lineno === 1
    )
  })
}
